import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useModalService } from '~/common/hooks/useModalService';
import { IImage, IS3File } from '~/types/model.types';
import { useRootStore } from '~/stores/useRootStore';
import { TImageUploadType } from '~/types/server.types';

export type IAttachAssetModalReturnType = {
    newAssets: TUploadedFile[];
    existingAssets: IImage[] | IS3File[];
};

export type TUploadedFile = File & { _id: string; dataUrl: string };

type ModalOptions = (string | boolean | number | IImage)[];

const DEFAULT_MODAL_OPTIONS: ModalOptions = [
    false, // showCropTab
    true, // showGalleryTab
    true, // showUploadTab
    'Library', // defaultTab
    true, // allowMultipleAttach
    true, // showBackdrop
    '', // selectedImage
    null, // aspectRatio
    false, // showPreviewWhenCropping
    false, // startUploadFile
    '', // acceptFilesFilter
    '', // model
    'sample-class', // customCssClass
    false, // showCropTitle
    '', // footNoteText
    false, // branded
    '', // footNoteTextUploadTabDesktop
    '' // footNoteTextUploadTabMobile
];

const useAssetLibrary = () => {
    const {
        companyStore: { currCompany },
        imageCtrl,
        s3UploadService
    } = useRootStore();

    const openAttachAssetModal = useModalService({
        modalServiceMethodName: 'openAttachAssetModal'
    });

    const handleOpenAttachAssetsModal = useCallback(
        async (
            modalOptions = DEFAULT_MODAL_OPTIONS
        ): Promise<IAttachAssetModalReturnType | IImage> => {
            const assets = await openAttachAssetModal(modalOptions);
            if (Array.isArray(assets)) {
                const newAssets = assignIds(
                    assets.filter(asset => asset instanceof File)
                );
                const existingAssets = assets.filter(
                    asset => !(asset instanceof File)
                );

                return { newAssets, existingAssets };
            }
            return assets;
        },
        [openAttachAssetModal]
    );

    function assignIds(newAssets) {
        return newAssets.map(asset => Object.assign(asset, { _id: uuidv4() }));
    }

    const uploadNewFiles = useCallback(
        async ({
            files,
            isOneComposer = false,
            setUploadProgress,
            uploadType
        }: {
            files: TUploadedFile[];
            isOneComposer?: boolean;
            setUploadProgress: (progress: number, file: TUploadedFile) => void;
            uploadType: TImageUploadType;
        }) => {
            const uploadPromises = files.map(file =>
                file.type.includes('image')
                    ? imageCtrl.uploadImage({
                          file,
                          setUploadProgress,
                          uploadType
                      })
                    : s3UploadService.uploadFile(
                          file,
                          setUploadProgress,
                          currCompany._id
                      )
            );

            return isOneComposer
                ? Promise.allSettled(uploadPromises)
                : Promise.all(uploadPromises);
        },
        [currCompany._id, imageCtrl, s3UploadService]
    );

    return {
        openAttachAssetModal: handleOpenAttachAssetsModal,
        uploadNewFiles
    };
};

export default useAssetLibrary;
